import React, { useState, useEffect } from 'react';
import ApiService from '../services/api';
import { Child } from '../types';
import './SOCSSetupModal.css';

interface SpondSetupModalProps {
  isOpen: boolean;
  onClose: (completed: boolean) => void;
  children: Child[];
  suggestedEmails?: string[];
  defaultEmail?: string;
  onEmailUsed?: (email: string) => void;
}

interface SpondMember {
  id: string;
  firstName: string;
  lastName: string;
  guardians: { id: string; firstName: string; lastName: string }[];
}

interface SpondGroup {
  id: string;
  name: string;
  activity: string | null;
  imageUrl: string | null;
  members: SpondMember[];
}

const SpondSetupModal: React.FC<SpondSetupModalProps> = ({
  isOpen,
  onClose,
  children: cluubzChildren,
  suggestedEmails = [],
  defaultEmail = '',
  onEmailUsed
}) => {
  const apiService = ApiService.getInstance();

  // Steps: 1 = Credentials, 2 = Connecting, 3 = Review & Import
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Step 1: Credentials
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showCustomEmailInput, setShowCustomEmailInput] = useState(false);

  // Step 3: Groups & mappings review
  const [groups, setGroups] = useState<SpondGroup[]>([]);
  const [excludedGroups, setExcludedGroups] = useState<{ [groupId: string]: boolean }>({});
  const [childMappings, setChildMappings] = useState<{ [memberKey: string]: string }>({});

  // Track previous isOpen state
  const wasOpen = React.useRef(false);

  // Reset state when modal opens fresh
  useEffect(() => {
    if (isOpen) {
      if (!wasOpen.current) {
        setStep(1);
        setError(null);
        setEmail(defaultEmail || '');
        setPassword('');
        setShowPassword(false);
        setShowCustomEmailInput(false);
        setGroups([]);
        setExcludedGroups({});
        setChildMappings({});
      }
      wasOpen.current = true;
    } else {
      wasOpen.current = false;
    }
  }, [isOpen, defaultEmail]);

  const levenshteinDistance = (str1: string, str2: string): number => {
    const matrix: number[][] = [];
    for (let i = 0; i <= str2.length; i++) matrix[i] = [i];
    for (let j = 0; j <= str1.length; j++) matrix[0][j] = j;
    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }
    return matrix[str2.length][str1.length];
  };

  const fuzzyMatchScore = (spondName: string, cluubzName: string): number => {
    const s = spondName.toLowerCase().trim();
    const c = cluubzName.toLowerCase().trim();
    if (s === c) return 1.0;

    const sParts = s.split(/\s+/);
    const cParts = c.split(/\s+/);
    const sFirst = sParts[0] || '';
    const cFirst = cParts[0] || '';
    const sLast = sParts.slice(1).join(' ');
    const cLast = cParts.slice(1).join(' ');

    const firstDistance = levenshteinDistance(sFirst, cFirst);
    const firstMaxLen = Math.max(sFirst.length, cFirst.length);
    const firstScore = firstMaxLen > 0 ? (1 - (firstDistance / firstMaxLen)) : 0;

    let lastScore = 0;
    if (sLast && cLast) {
      const lastDistance = levenshteinDistance(sLast, cLast);
      const lastMaxLen = Math.max(sLast.length, cLast.length);
      lastScore = 1 - (lastDistance / lastMaxLen);
    } else if (sLast || cLast) {
      lastScore = firstScore > 0.8 ? 0.5 : 0;
    } else {
      lastScore = 1.0;
    }

    return Math.max(0, (firstScore * 0.7) + (lastScore * 0.3));
  };

  const autoMapChildren = (groups: SpondGroup[]) => {
    const mappings: { [key: string]: string } = {};
    const MATCH_THRESHOLD = 0.6;
    for (const group of groups) {
      for (const member of group.members) {
        const spondFullName = `${member.firstName} ${member.lastName}`.trim();
        let bestMatch: Child | null = null;
        let bestScore = 0;
        for (const child of cluubzChildren) {
          const score = fuzzyMatchScore(spondFullName, child.name);
          if (score > bestScore) {
            bestScore = score;
            bestMatch = child;
          }
        }
        if (bestMatch && bestScore >= MATCH_THRESHOLD) {
          mappings[`${group.id}:${member.id}`] = bestMatch.uuid;
        }
      }
    }
    setChildMappings(mappings);
  };

  const handleConnect = async () => {
    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }

    setLoading(true);
    setError(null);
    setStep(2);

    try {
      const response = await apiService.spondLogin(email, password);

      if (response.success && response.data) {
        setGroups(response.data.groups);
        autoMapChildren(response.data.groups);

        if (onEmailUsed && email && !suggestedEmails.includes(email)) {
          onEmailUsed(email);
        }

        setStep(3);
      } else {
        setError(response.error || 'Login failed. Please check your credentials.');
        setStep(1);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to connect to Spond');
      setStep(1);
    } finally {
      setLoading(false);
    }
  };

  const toggleGroupExclusion = (groupId: string) => {
    setExcludedGroups(prev => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const isGroupExcluded = (groupId: string) => excludedGroups[groupId] === true;

  const handleApproveAndImport = async () => {
    // Build selected groups and mappings
    const selectedGroups = groups.filter(g => !isGroupExcluded(g.id));

    const mappingsArray = Object.entries(childMappings)
      .filter(([key, childUuid]) => {
        const [groupId] = key.split(':');
        return childUuid && !isGroupExcluded(groupId);
      })
      .map(([key, childUuid]) => {
        const [groupId, memberId] = key.split(':');
        const group = groups.find(g => g.id === groupId);
        const member = group?.members.find(m => m.id === memberId);
        return {
          groupId,
          spondMemberId: memberId,
          spondMemberName: member ? `${member.firstName} ${member.lastName}` : '',
          childUuid
        };
      });

    if (mappingsArray.length === 0) {
      setError('Please map at least one member to a child');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await apiService.spondSetup(
        email, password, selectedGroups, mappingsArray
      );

      if (response.success) {
        onClose(true);
      } else {
        setError(response.error || 'Import failed');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to import activities');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="socs-modal-overlay">
      <div className="socs-modal">
        <div className="socs-modal-header" style={{
          background: 'linear-gradient(135deg, #1a73e8 0%, #0d47a1 100%)',
          borderRadius: '8px 8px 0 0'
        }}>
          <h2 style={{ color: 'white', display: 'flex', alignItems: 'center', gap: '10px' }}>📱 Connect Spond</h2>
          <button className="close-btn" onClick={() => onClose(false)} style={{ color: 'white', opacity: 0.8 }}>×</button>
        </div>

        <div className="socs-modal-content">
          {error && (
            <div className="error-message" style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '4px' }}>
              {error}
            </div>
          )}

          {/* Step 1: Credentials */}
          {step === 1 && (
            <>
              <p style={{ marginBottom: '16px' }}>
                Enter your Spond login details. We'll discover your groups, match your children,
                and import all fixtures and events.
              </p>

              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label>Spond Email</label>
                {suggestedEmails.length > 0 && !showCustomEmailInput ? (
                  <select
                    value={email}
                    onChange={(e) => {
                      if (e.target.value === '__custom__') {
                        setShowCustomEmailInput(true);
                        setEmail('');
                      } else {
                        setEmail(e.target.value);
                      }
                    }}
                    disabled={loading}
                    style={{ width: '100%', padding: '10px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '14px', backgroundColor: 'white' }}
                  >
                    <option value="">Select an email...</option>
                    {suggestedEmails.map((em, idx) => (
                      <option key={idx} value={em}>{em}</option>
                    ))}
                    <option value="__custom__">+ Add different email</option>
                  </select>
                ) : (
                  <div>
                    <input
                      type="text"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="form-input"
                      placeholder="your.email@example.com"
                      disabled={loading}
                      autoComplete="off"
                    />
                    {suggestedEmails.length > 0 && (
                      <button
                        type="button"
                        onClick={() => {
                          setShowCustomEmailInput(false);
                          setEmail(defaultEmail || suggestedEmails[0] || '');
                        }}
                        style={{
                          marginTop: '8px',
                          padding: '4px 10px',
                          fontSize: '12px',
                          color: '#1a73e8',
                          background: 'none',
                          border: '1px solid #1a73e8',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        Back to saved emails
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label>Spond Password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="form-input"
                    placeholder="Your Spond password"
                    disabled={loading}
                    autoComplete="new-password"
                    onKeyDown={e => e.key === 'Enter' && handleConnect()}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '16px'
                    }}
                  >
                    {showPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
              </div>

              <button
                onClick={handleConnect}
                disabled={loading || !email || !password}
                style={{
                  width: '100%',
                  padding: '12px 24px',
                  backgroundColor: (!loading && email && password) ? '#1a73e8' : '#ccc',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: (!loading && email && password) ? 'pointer' : 'not-allowed',
                  fontWeight: 600,
                  marginTop: '8px'
                }}
              >
                {loading ? '⏳ Connecting...' : '🔗 Connect Spond'}
              </button>

              <p style={{ fontSize: '12px', color: '#666', textAlign: 'center', marginTop: '12px' }}>
                🔒 Your credentials are encrypted and stored securely
              </p>
            </>
          )}

          {/* Step 2: Connecting */}
          {step === 2 && (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <div style={{
                width: '50px',
                height: '50px',
                border: '4px solid #e0e0e0',
                borderTop: '4px solid #1a73e8',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 20px'
              }} />
              <style>{`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}</style>
              <p style={{ fontSize: '16px', fontWeight: 500 }}>
                Connecting to Spond...
              </p>
              <p style={{ fontSize: '14px', color: '#666' }}>
                Discovering groups and matching children
              </p>
            </div>
          )}

          {/* Step 3: Review Groups & Mappings */}
          {step === 3 && (
            <>
              <p style={{ marginBottom: '16px' }}>
                We found your groups and matched your children.
                Verify these are correct and choose which to import:
              </p>

              <div style={{ maxHeight: '400px', overflowY: 'auto', marginBottom: '16px' }}>
                {groups.map((group) => (
                  <div
                    key={group.id}
                    style={{
                      border: isGroupExcluded(group.id) ? '1px solid #ccc' : '1px solid #e0e0e0',
                      borderRadius: '8px',
                      padding: '12px',
                      marginBottom: '12px',
                      backgroundColor: isGroupExcluded(group.id) ? '#f5f5f5' : 'white',
                      opacity: isGroupExcluded(group.id) ? 0.7 : 1
                    }}
                  >
                    <div style={{ marginBottom: '8px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', flexWrap: 'wrap' }}>
                        <div>
                          <strong style={{ color: isGroupExcluded(group.id) ? '#999' : 'inherit' }}>
                            {group.name}
                          </strong>
                          {group.activity && (
                            <span style={{ display: 'block', fontSize: '13px', color: '#888', marginTop: '2px' }}>
                              {group.activity}
                            </span>
                          )}
                        </div>
                        <label
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            color: isGroupExcluded(group.id) ? '#dc3545' : '#28a745',
                            flexShrink: 0
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={!isGroupExcluded(group.id)}
                            onChange={() => toggleGroupExclusion(group.id)}
                            style={{ cursor: 'pointer' }}
                          />
                          {isGroupExcluded(group.id) ? "Don't import" : 'Import fixtures'}
                        </label>
                      </div>
                    </div>

                    {!isGroupExcluded(group.id) && group.members.length > 0 && (
                      <div style={{
                        padding: '8px 10px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '4px',
                        fontSize: '13px'
                      }}>
                        <div style={{ fontWeight: 500, marginBottom: '6px', color: '#555' }}>
                          📱 Your children in this group:
                        </div>
                        {group.members.map(member => (
                          <div key={member.id} style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '6px 0',
                            borderBottom: '1px solid #eee'
                          }}>
                            <span style={{ flex: 1 }}>
                              {member.firstName} {member.lastName}
                            </span>
                            <span style={{ color: '#666', fontSize: '13px' }}>Maps to:</span>
                            <select
                              value={childMappings[`${group.id}:${member.id}`] || ''}
                              onChange={(e) => setChildMappings(prev => ({
                                ...prev,
                                [`${group.id}:${member.id}`]: e.target.value
                              }))}
                              style={{
                                padding: '6px 10px',
                                borderRadius: '4px',
                                border: '1px solid #ccc',
                                fontSize: '13px'
                              }}
                            >
                              <option value="">-- Skip --</option>
                              {cluubzChildren.map((child) => (
                                <option key={child.uuid} value={child.uuid}>
                                  {child.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        ))}
                      </div>
                    )}

                    {!isGroupExcluded(group.id) && group.members.length === 0 && (
                      <div style={{
                        padding: '8px 10px',
                        backgroundColor: '#fff3cd',
                        borderRadius: '4px',
                        fontSize: '13px',
                        color: '#856404'
                      }}>
                        ⚠️ No children found in this group for your account
                      </div>
                    )}

                    {isGroupExcluded(group.id) && (
                      <div style={{ fontSize: '13px', color: '#999', fontStyle: 'italic' }}>
                        This group's fixtures will not be imported
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <button
                onClick={handleApproveAndImport}
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '12px 24px',
                  backgroundColor: loading ? '#ccc' : '#1a73e8',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontWeight: 600
                }}
              >
                {loading ? '⏳ Importing...' : '✓ Approve & Import Activities'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SpondSetupModal;
